import { useEffect, useMemo, useState, useRef } from 'react';
import { Action, BooleanField, Button, Column, FieldType, FieldValue, ModalId, O, Table2, useEffectAfterMount, useUIActions } from '../../../ui';
import { DataType } from '../../../../tables';
import { Params } from '../../../../state/reducers/types';
import { ListResponse } from '../../../../modules/common';
import { Data, Data2 } from '../../types';
import { RoutePath, useRoutesHooks } from '../../../routes';
import { useLocation } from 'react-router-dom';
import { RemoveModal } from '../remove-modal/removeModal';
import { dedupe, toList } from '../../../../utils/listUtils';
import { BulkEditModal } from '../bulk-edit-modal/bulkEditModal';
import { BulkAction } from '../bulk-edit-modal/types';
import { ArchiveModal } from '../remove-modal/archiveModal';
import { INITIAL_SORT_STATE, SortState, nextSortState, sortRows, useTableSortLabels, withSortHeaders } from './sortUtils';

type Props = {
    columns: Column[]
    rowId?: string
    filtersText?: string
    bulkActions?: BulkAction[]
    currentDataTypeParam?: DataType
    readOnly: boolean
    toRow: (dataType: DataType) => Data2
    loadBatch: (startKey?: Params, dbRequest?: boolean) => Promise<ListResponse>
    count: (startKey?: Params) => Promise<number>
    actions?: (rowIdValue: string) => Promise<Action[]>
}

export const DataTypeList2 = (props: Props) => {
    const { columns, rowId = 'id', filtersText, bulkActions = [], currentDataTypeParam, readOnly, toRow, loadBatch, count, actions } = props

    const { tableName } = useRoutesHooks().parseUrl()

    const [dataTypes, setDataTypes] = useState<DataType[]>([])
    const [allItems, setAllItems] = useState<DataType[]>([])
    const [allLoaded, setAllLoaded] = useState(false)
    const [inferredPageSize, setInferredPageSize] = useState(10)
    const [currentDataType, setCurrentDataType] = useState<O<DataType>>(currentDataTypeParam)
    const [lastKey, setLastKey] = useState<O<Params>>()
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [pagesData, setPagesData] = useState<Map<number, DataType[]>>(new Map())
    const [disableRefresh, setDisableRefresh] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [showNext, setShowNext] = useState<boolean>(false)
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [allSelected, setAllSelected] = useState<boolean>(false)
    const [sortState, setSortState] = useState<SortState>(INITIAL_SORT_STATE)
    const currentRequestId = useRef<number>(0)

    useEffect(() => {
        setCurrentDataType(currentDataTypeParam)
    }, [currentDataTypeParam])

    const onToggleCheckAll = (value?: boolean) => {
        const pageIds = pagesData.get(currentPage)?.map(dataType => dataType.id) || []
        setAllSelected(!!value)
        setSelectedIds(prevIds => value ?
            dedupe(prevIds.concat(pageIds)) :
            prevIds.filter(prevId => !pageIds.includes(prevId)))
    }

    const onToggleCheck = (id: string) => (value?: boolean) => {
        setSelectedIds(prevIds => value ? prevIds.concat(id) : prevIds.filter(prevId => prevId !== id))
    }

    const baseTableColumns: Column[] = useMemo(() => {
        const checkColumn: Column = {
            name: 'check',
            type: FieldType.BOOLEAN,
            label: '',
            sortable: false,
            renderLabel: () => (
                <BooleanField
                    id="check-all"
                    value={allSelected}
                    onChange={onToggleCheckAll}
                />
            ),
            render: (value?: FieldValue) => {
                const rowIdValue = value! as string
                return (
                    <BooleanField
                        id={`check-${rowIdValue}`}
                        value={selectedIds.includes(rowIdValue)}
                        onChange={onToggleCheck(rowIdValue)}
                    />
                )
            }
        }
        return bulkActions.length > 0 ? toList(checkColumn).concat(columns) : columns
    }, [columns, selectedIds, onToggleCheckAll, onToggleCheck, pagesData, currentPage])

    const toDisplayRow = (dataType: DataType): Data => ({
        ...toRow(dataType),
        ...(bulkActions.length > 0 && {
            check: dataType[rowId as keyof DataType].toString()
        })
    })

    const sortColumn = columns.find(c => c.name === sortState.column)
    const allSortRows = useMemo(() => sortState.direction ? allItems.map(toDisplayRow) : [], [allItems, sortState.direction])
    const tableSortLabels = useTableSortLabels(allSortRows, sortColumn)

    const rows = useMemo((): Data[] => {
        if (!sortState.column || !sortState.direction) {
            return dataTypes.map(toDisplayRow)
        }
        if (!sortColumn || sortColumn.sortable === false) return dataTypes.map(toDisplayRow)

        const sorted = sortRows(allSortRows, sortColumn, sortState.direction, tableSortLabels)
        const start = (currentPage - 1) * inferredPageSize
        return sorted.slice(start, start + inferredPageSize)
    }, [dataTypes, allSortRows, sortState, sortColumn, currentPage, inferredPageSize, tableSortLabels])

    const { pathname } = useLocation()

    const { openModal } = useUIActions()

    const loadAllRemainingItems = async (currentLastKey: O<Params>, currentAllItems: DataType[]) => {
        let key = currentLastKey
        const newItems: DataType[] = []
        while (key) {
            const { dataTypes: more, lastKey: next } = await loadBatch(key)
            newItems.push(...more)
            key = next
        }
        const all = [...currentAllItems, ...newItems]
        setAllItems(all)
        setLastKey(undefined)
        setAllLoaded(true)
        return all
    }

    const onColumnSort = async (columnName: string) => {
        const newSort = nextSortState(sortState, columnName)
        if (newSort.column && !allLoaded) {
            setLoading(true)
            try {
                await loadAllRemainingItems(lastKey, allItems)
            } finally {
                setLoading(false)
            }
        }
        setSortState(newSort)
        setCurrentPage(1)
    }

    const load = async (page: number, newLastKey?: Params, dbRequest = false) => {
        setLoading(true)
        setCurrentPage(page)
        const response = await loadBatch(newLastKey, dbRequest)
        setLastKey(response.lastKey)
        const loadedDataTypes = response.dataTypes
        setDataTypes(loadedDataTypes)
        if (page === 1) {
            setInferredPageSize(loadedDataTypes.length || 10)
            setPagesData(new Map().set(page, loadedDataTypes))
            setAllItems(loadedDataTypes)
            setAllLoaded(!response.lastKey)
        } else {
            setPagesData(prevPagesData => prevPagesData.set(page, loadedDataTypes))
            setAllItems(prev => [...prev, ...loadedDataTypes])
            if (!response.lastKey) setAllLoaded(true)
        }
        setLoading(false)
    }

    const loadFirstPage = async (dbRequest = false) => {
        setLastKey(undefined)
        setSortState(INITIAL_SORT_STATE)
        setAllItems([])
        setAllLoaded(false)
        await load(1, undefined, dbRequest)
    }

    useEffect(() => {
        loadFirstPage(true)
    }, [])

    useEffectAfterMount(() => {
        const timeoutId = setTimeout(loadFirstPage, 300)
        return () => clearTimeout(timeoutId)
    }, [filtersText])

    const loadShowNext = async () => {
        return (pagesData.get(currentPage + 1)?.length || 0) > 0 || (
            !!lastKey && (await count(lastKey) > 0)
        )
    }

    useEffect(() => {
        const requestId = ++currentRequestId.current
        loadShowNext().then(result => requestId === currentRequestId.current && setShowNext(result))
    }, [currentPage, pagesData, lastKey])

    const effectiveShowNext = sortState.column
        ? currentPage * inferredPageSize < allItems.length
        : showNext

    const goToNextPage = async () => {
        if (sortState.column) {
            setCurrentPage(prev => prev + 1)
            return
        }
        setLoading(true)
        const newPage = currentPage + 1
        setCurrentPage(newPage)
        const pageData = pagesData.get(newPage)
        if (pageData) {
            setDataTypes(pageData)
            setLoading(false)
        } else {
            await load(newPage, lastKey)
        }
        setAllSelected(false)
    }

    const goToPreviousPage = () => {
        if (sortState.column) {
            setCurrentPage(prev => prev - 1)
            return
        }
        setLoading(true)
        const newPage = currentPage - 1
        setCurrentPage(newPage)
        setDataTypes(pagesData.get(newPage) || [])
        setAllSelected(false)
        setLoading(false)
    }

    const find = (id?: string) => dataTypes.find(dataType => dataType.id === id)

    const onRefresh = () => {
        setDisableRefresh(true)
        loadFirstPage(true)
        setTimeout(() => setDisableRefresh(false), 10000)
    }

    const onBulkEdit = () => openModal(ModalId.BULK_EDIT)

    const basePath = pathname.replace(/\/$/, '')

    const defaultOnRemove = (id?: string) => {
        setCurrentDataType(find(id!))
        openModal(ModalId.REMOVE)
    }

    const defaultActions = async (rowIdValue: string): Promise<Action[]> => {
        const actionList: Action[] = [
            {
                icon: 'eye-fill',
                kind: 'tertiary',
                tooltip: 'Ver Detalle',
                href: `${basePath}/${rowIdValue}`
            }
        ]
        if (!readOnly) {
            const editAction: Action = {
                icon: 'pencil-fill',
                tooltip: 'Editar',
                href: `${basePath}/${rowIdValue}/${RoutePath.EDIT}`
            }
            const removeAction: Action = {
                icon: 'trash-fill',
                kind: 'secondary',
                tooltip: 'Eliminar',
                onClick: defaultOnRemove
            }
            actionList.push(editAction, removeAction)
        }

        return actionList
    }

    const renderHeaderActions = () => {
        const headerActions = [
            <Button
                key="refresh"
                kind="tertiary"
                icon="arrow-clockwise"
                tooltip="Refrescar"
                disabled={disableRefresh}
                round
                rightMargin={!readOnly}
                onClick={onRefresh}
            />
        ]
        const selectedAmount = selectedIds.length
        if (selectedAmount > 0) {
            headerActions.splice(0, 0, (
                <Button
                    key="bulk"
                    kind="primary"
                    label={selectedAmount.toString()}
                    icon="list-check"
                    rightMargin={!readOnly}
                    onClick={onBulkEdit}
                />
            ))
        }
        if (!readOnly) {
            headerActions.push(
                <Button
                    key="create"
                    label="Crear Nuevo"
                    href={`${basePath}/${RoutePath.CREATE}`}
                />
            )
        }
        return headerActions
    }

    const sortableTableColumns = withSortHeaders(baseTableColumns, sortState, onColumnSort)

    return (
        <>
            <Table2
                columns={sortableTableColumns}
                rows={rows as Data[]}
                rowId={rowId}
                readOnly={readOnly}
                currentPage={currentPage}
                showNext={effectiveShowNext}
                loading={loading}
                actions={actions || defaultActions}
                renderHeaderActions={renderHeaderActions}
                onGoToPreviousPage={goToPreviousPage}
                onGoToNextPage={goToNextPage}
            />
            {bulkActions.length > 0 && (
                <BulkEditModal
                    ids={selectedIds}
                    bulkActions={bulkActions}
                />
            )}
            <RemoveModal
                tableName={tableName!}
                dataType={currentDataType}
            />
            <ArchiveModal
                tableName={tableName!}
                dataType={currentDataType}
            />
        </>
    )
}
